import styles from "./styles/GraphicsManager.module.css";
import React, { useState, useMemo, useEffect } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { FileImageOutlined } from "@ant-design/icons";
import { useAllFiles } from "../_actions/app/files";
import { LoadingOutlined } from "@ant-design/icons";
import { getImageFileURL } from "../_helpers/image";
import {
    Spin,
    Button,
    Segmented,
    Select,
    Modal,
    Checkbox,
    message,
    Pagination,
    Form,
} from "antd";
import { useItemsFine } from "../actions/app/items";
import {
    useUpdateItemMutation,
    useCreateItemMutation,
} from "../_actions/app/items";
import ThemeGenerator from "../sandbox/ThemeGenerator";

const FILTERS = {
    SVG: "SVG",
    PNG: "PNG",
};

const FILTERS_TYPE = {
    All: "ALL",
    Public: "PUBLIC",
    Private: "PRIVATE",
};

const MIMETYPE_OPTIONS = [
    { label: "PNG", value: "image/png" },
    { label: "JPG", value: "image/jpg" },
    { label: "JPEG", value: "image/jpeg" },
    { label: "WEBP", value: "image/webp" },
];

function GraphicsManager() {
    const [filters, setFilters] = useState("SVG");
    const [selected_type, setSelectedType] = useState("All");
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [mimeType, setMimeType] = useState("image/png");
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [hoveredImageId, setHoveredImageId] = useState(null);
    const [collectionToSend, setCollectionToSend] = useState(null);

    const {
        data: collectionsData,
        isLoading: collectionsLoading,
        isSuccess,
    } = useItemsFine("qdb_asset_collection", "fields=id,name");

    useEffect(() => {
        if (selectedCollection) {
            setSelectedType("All");
        }
    }, [selectedCollection]);

    const queryString = useMemo(() => {
        const parts = [];

        if (filters === "SVG") {
            parts.push("filter[type][_eq]=image/svg%2Bxml");
        } else if (filters === "PNG") {
            if (mimeType) {
                parts.push(`filter[type][_eq]=${encodeURIComponent(mimeType)}`);
            } else {
                const allowedTypes = [
                    "image/png",
                    "image/jpg",
                    "image/jpeg",
                    "image/webp",
                ];
                const encodedTypes = allowedTypes.map((t) =>
                    encodeURIComponent(t)
                );
                parts.push(`filter[type][_in]=${encodedTypes.join(",")}`);
            }
        }

        if (!selectedCollection) {
            if (selected_type === "Public") {
                parts.push("filter[public][_eq]=true");
            } else if (selected_type === "Private") {
                parts.push("filter[public][_eq]=false");
            }
        }

        if (selectedCollection) {
            parts.push(
                `filter[collection][_eq]=${encodeURIComponent(
                    selectedCollection
                )}`
            );
        }

        return parts.join("&");
    }, [filters, selected_type, selectedCollection, mimeType]);

    const [offset, setOffset] = useState(0);

    const {
        data: Allfiles,
        isLoading,
        error,
    } = useAllFiles(`${queryString}&offset=${offset}&limit=50`, true);

    const files = Allfiles?.data;
    const meta = Allfiles?.meta;

    const updateItemMutation = useUpdateItemMutation(() => {
        message.success("Images updated successfully!");
        setSelectedImages([]);
        setCollectionToSend(null);
    });

    const handleSelectAllToggle = () => {
        if (selectedImages.length === files.length) {
            setSelectedImages([]);
        } else {
            setSelectedImages(files.map((f) => f.id));
        }
    };

    const handleSelectImage = (id) => {
        setSelectedImages((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSendToCollection = () => {
        if (!collectionToSend || selectedImages.length === 0) {
            message.warning("Please select images and a collection.");
            return;
        }

        selectedImages.forEach((imageId) => {
            updateItemMutation.mutate(
                {
                    collection: "directus_files",
                    id: imageId,
                    data: {
                        collection: collectionToSend,
                        public: true, // Also mark as public
                    },
                },
                {
                    onError: (err) => {
                        console.error(err);
                        message.error(
                            `Failed: ${
                                err.response?.data?.errors?.[0]?.message ||
                                err.message
                            }`
                        );
                    },
                }
            );
        });
    };

    const handleSendToPublic = () => {
        if (selectedImages.length === 0) {
            message.warning("Please select images.");
            return;
        }

        selectedImages.forEach((imageId) => {
            updateItemMutation.mutate(
                {
                    collection: "directus_files",
                    id: imageId,
                    data: { public: true },
                },
                {
                    onError: (err) => {
                        console.error(err);
                        message.error(
                            `Failed: ${
                                err.response?.data?.errors?.[0]?.message ||
                                err.message
                            }`
                        );
                    },
                }
            );
        });
    };

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState("");
    const createCollectionMutation = useCreateItemMutation(() => {
        message.success("Collection added successfully!");
        setIsAddModalOpen(false);
        setNewCollectionName("");
    });

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading="Graphics Manager"
                    icon={<FileImageOutlined />}
                />
            }
        >
            <div className={styles.wrapper}>
                {/* Sidebar */}
                <div className={styles.sidebar}>
                    <h3>Collections</h3>
                    {collectionsLoading && <p>Loading collections...</p>}
                    <div className={styles.collectionGrid}>
                        {/* + Add Collection Button */}

                        <Button
                            className={styles.addCollectionButton}
                            type="dashed"
                            onClick={() => setIsAddModalOpen(true)}
                        >
                            + Add Collection
                        </Button>
                        {/* All button */}
                        <Button
                            type={
                                selectedCollection === null
                                    ? "primary"
                                    : "default"
                            }
                            onClick={() => setSelectedCollection(null)}
                        >
                            All
                        </Button>

                        {/* Individual collections */}
                        {isSuccess &&
                            [...collectionsData]
                                .sort((a, b) => a.name.localeCompare(b.name)) //  alphabetical sort
                                .map((item) => (
                                    <Button
                                        key={item.id}
                                        type={
                                            selectedCollection === item.id
                                                ? "primary"
                                                : "default"
                                        }
                                        onClick={() =>
                                            setSelectedCollection(
                                                selectedCollection === item.id
                                                    ? null
                                                    : item.id
                                            )
                                        }
                                    >
                                        {item.name}
                                    </Button>
                                ))}
                    </div>
                </div>

                {/* Content */}
                <div className={styles.content}>
                    <div className={styles.wrapperFilter}>
                        <ThemeGenerator
                            trigger={<Button>Theme Generator</Button>}
                        />

                        <GraphicsFilters
                            value={filters}
                            onChange={setFilters}
                            selected_type={selected_type}
                            onTypeChange={setSelectedType}
                            mimeType={mimeType}
                            setMimeType={setMimeType}
                            collectionSelected={!!selectedCollection}
                        />
                        <div className={styles.selectButtonContainer}>
                            <Button onClick={handleSelectAllToggle}>
                                {selectedImages.length === files?.length
                                    ? "Deselect All"
                                    : "Select All"}
                            </Button>

                            {selectedImages.length > 0 && (
                                <div
                                    className={styles.sendToCollectionAndPublic}
                                >
                                    <Select
                                        className={styles.selectCollection}
                                        placeholder="Select Collection"
                                        value={collectionToSend}
                                        onChange={setCollectionToSend}
                                        options={
                                            collectionsData
                                                ?.slice() // copy array
                                                .sort((a, b) =>
                                                    a.name.localeCompare(b.name)
                                                ) // sort
                                                .map((col) => ({
                                                    label: col.name,
                                                    value: col.id,
                                                })) || []
                                        }
                                    />
                                    <Button
                                        type="primary"
                                        onClick={handleSendToCollection}
                                        loading={updateItemMutation.isLoading}
                                    >
                                        Add to Collection
                                    </Button>

                                    {selected_type !== "Public" && (
                                        <Button
                                            type="primary"
                                            onClick={handleSendToPublic}
                                            loading={
                                                updateItemMutation.isLoading
                                            }
                                        >
                                            Make Public
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Loading */}
                    {isLoading && (
                        <div className={styles.loadingContainer}>
                            <Spin
                                indicator={<LoadingOutlined spin />}
                                size="large"
                            />
                            <p>Loading your images...</p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className={styles.errorContainer}>
                            <p>Error loading images: {error.message}</p>
                        </div>
                    )}

                    {/* Empty */}
                    {!isLoading && !error && files?.length === 0 && (
                        <div className={styles.emptyState}>
                            <FileImageOutlined className={styles.emptyIcon} />
                            <p>No images found</p>
                        </div>
                    )}

                    {/* Images Grid */}
                    {!isLoading && !error && files?.length > 0 && (
                        <div className={styles.imageGrid}>
                            {files.map((file) => {
                                const isChecked = selectedImages.includes(
                                    file.id
                                );

                                const handleCardClick = () => {
                                    if (selectedImages.length > 0) {
                                        // Toggle selection if at least one is already selected
                                        handleSelectImage(file.id);
                                    } else {
                                        // Open preview if none selected
                                        setPreviewImage(file);
                                    }
                                };

                                return (
                                    <div
                                        key={file.id}
                                        className={`${styles.imageCard} ${
                                            isChecked
                                                ? styles.selectedImage
                                                : ""
                                        }`}
                                        onMouseEnter={() =>
                                            setHoveredImageId(file.id)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredImageId(null)
                                        }
                                        onClick={handleCardClick}
                                    >
                                        <div
                                            className={`${
                                                styles.radioWrapper
                                            } ${
                                                hoveredImageId === file.id ||
                                                isChecked
                                                    ? styles.radioVisible
                                                    : ""
                                            }`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Checkbox
                                                className={styles.checkbox}
                                                checked={isChecked}
                                                onChange={() =>
                                                    handleSelectImage(file.id)
                                                }
                                            />
                                        </div>
                                        <img
                                            src={getImageFileURL(
                                                file.filename_disk
                                            )}
                                            alt="graphic"
                                            className={styles.imageThumb}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                        <Pagination
                            current={Math.floor(offset / 50) + 1}
                            pageSize={50}
                            total={meta?.filter_count || 0}
                            onChange={(page) => setOffset((page - 1) * 50)}
                            showSizeChanger={false}
                            showQuickJumper={true}
                        />
                        {/* <div className={styles.pageInfo}>
                            of {Math.ceil((meta?.filter_count || 0) / 50)}
                        </div> */}
                    </div>
                </div>

                <Modal
                    title="Add New Collection"
                    open={isAddModalOpen}
                    onCancel={() => setIsAddModalOpen(false)}
                    footer={null}
                >
                    <div className={styles.addCollectionModal}>
                        <input
                            className={styles.addCollectionInput}
                            type="text"
                            placeholder="Collection Name"
                            value={newCollectionName}
                            onChange={(e) =>
                                setNewCollectionName(e.target.value)
                            }
                        />
                        <Button
                            type="primary"
                            loading={createCollectionMutation.isLoading}
                            onClick={() => {
                                if (!newCollectionName.trim()) {
                                    message.warning(
                                        "Please enter a collection name."
                                    );
                                    return;
                                }
                                createCollectionMutation.mutate(
                                    {
                                        collection: "qdb_asset_collection",
                                        data: { name: newCollectionName },
                                    },
                                    {
                                        onSuccess: () => {
                                            message.success(
                                                "Collection added successfully!"
                                            );
                                            setIsAddModalOpen(false); // Modal close
                                            setNewCollectionName(""); // Input clear
                                        },
                                        onError: (err) => {
                                            message.error(
                                                "Failed to add collection"
                                            );
                                            console.error(err);
                                        },
                                    }
                                );
                            }}
                        >
                            Add
                        </Button>
                    </div>
                </Modal>

                {/* Preview Modal */}
                <Modal
                    open={!!previewImage}
                    footer={null}
                    onCancel={() => setPreviewImage(null)}
                    width={750}
                >
                    {previewImage && (
                        <div className={styles.previewModalContainer}>
                            <div className={styles.previewImageWrapper}>
                                <img
                                    src={getImageFileURL(
                                        previewImage.filename_disk
                                    )}
                                    alt="preview"
                                    className={styles.previewImage}
                                />
                            </div>
                            <div className={styles.previewDetails}>
                                <p>
                                    <strong>Title:</strong>{" "}
                                    {previewImage.title || "Untitled"}
                                </p>
                                <p>
                                    <strong>Mime Type:</strong>{" "}
                                    {previewImage.type || "N/A"}
                                </p>
                                <p>
                                    <strong>Hidden:</strong>{" "}
                                    {previewImage.hidden ? "Yes" : "No"}
                                </p>
                                <p>
                                    <strong>Collection:</strong>{" "}
                                    {previewImage.collection || "No collection"}
                                </p>

                                {/* Send to Collection & Public */}
                                <div className={styles.sendToCollectionModal}>
                                    <Select
                                        className={
                                            styles.collectionToSendSlectPreview
                                        }
                                        placeholder="Select Collection"
                                        value={collectionToSend}
                                        onChange={setCollectionToSend}
                                        options={
                                            collectionsData
                                                ?.slice() // copy array
                                                .sort((a, b) =>
                                                    a.name.localeCompare(b.name)
                                                ) // sort
                                                .map((col) => ({
                                                    label: col.name,
                                                    value: col.id,
                                                })) || []
                                        }
                                    />
                                    <Button
                                        className={
                                            styles.sendToCollectionButtonPreview
                                        }
                                        type="primary"
                                        block
                                        onClick={() => {
                                            if (!collectionToSend) {
                                                message.warning(
                                                    "Please select a collection."
                                                );
                                                return;
                                            }
                                            updateItemMutation.mutate(
                                                {
                                                    collection:
                                                        "directus_files",
                                                    id: previewImage.id,
                                                    data: {
                                                        collection:
                                                            collectionToSend,
                                                    },
                                                },
                                                {
                                                    onError: (err) => {
                                                        console.error(err);
                                                        message.error(
                                                            `Failed: ${
                                                                err.response
                                                                    ?.data
                                                                    ?.errors?.[0]
                                                                    ?.message ||
                                                                err.message
                                                            }`
                                                        );
                                                    },
                                                }
                                            );
                                        }}
                                    >
                                        Add to Collection
                                    </Button>

                                    {selected_type !== "Public" && (
                                        <Button
                                            type="default"
                                            block
                                            onClick={() =>
                                                updateItemMutation.mutate(
                                                    {
                                                        collection:
                                                            "directus_files",
                                                        id: previewImage.id,
                                                        data: { public: true },
                                                    },
                                                    {
                                                        onError: (err) => {
                                                            console.error(err);
                                                            message.error(
                                                                `Failed: ${
                                                                    err.response
                                                                        ?.data
                                                                        ?.errors?.[0]
                                                                        ?.message ||
                                                                    err.message
                                                                }`
                                                            );
                                                        },
                                                    }
                                                )
                                            }
                                        >
                                            Make Public
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </QurealSection>
    );
}

export default GraphicsManager;

function GraphicsFilters({
    value,
    onChange,
    selected_type,
    onTypeChange,
    mimeType,
    setMimeType,
    collectionSelected,
}) {
    return (
        <div className={styles.graphicsFiltersContainer}>
            <Segmented
                options={Object.keys(FILTERS)}
                value={value}
                onChange={(val) => onChange(val)}
            />
            <div className={styles.graphicsFiltersRight}>
                {value === "PNG" && (
                    <Select
                        className={styles.mimeTypeSelect}
                        placeholder="Mime Type"
                        allowClear
                        options={MIMETYPE_OPTIONS}
                        value={mimeType}
                        onChange={(v) => setMimeType(v)}
                    />
                )}
                <Segmented
                    options={Object.keys(FILTERS_TYPE).map((key) => ({
                        label: key,
                        value: key,
                        disabled:
                            collectionSelected &&
                            (key === "Public" || key === "Private"),
                    }))}
                    value={selected_type}
                    onChange={(val) => onTypeChange(val)}
                />
            </div>
        </div>
    );
}
